import React, { useState, useEffect } from 'react';
import './App.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import { AppShell, Burger, Group, Loader } from '@mantine/core';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './utils/queryClient';
import { CompetencyMap } from './pages/grades';
import { PerformanceReview } from './pages/performance-review';
import { PerformanceReviewRoundResults } from './pages/performance-review/_id/results';
import { PerformanceReviewAdmin } from './pages/performance-review/admin';
import { PerformanceReviewAdminRound } from './pages/performance-review/admin/_id';
import { PerformanceReviewQuestionnaires } from './pages/performance-review/admin/questionnaires';
import { Analytics } from './pages/analytics';
import { PerformanceReviewFormPage } from './pages/performance-review/forms/_id';
import { Dashboard } from './pages/dashboard';
import { GradeDetail } from './pages/competency-maps/_id/_gradeId';
import { UserProfile } from './pages/profile';
// import { ChangePassword } from './pages/ChangePassword';
import { Settings } from './pages/settings';
import OrganizationSettings from './pages/settings/organization';
import { Users } from './pages/users';
import { RolesPage } from './pages/roles';
import { Teams } from './pages/teams';
import { Navigation } from './components/app/Navigation';
import { Logo } from './components/app/Logo';
import { Professions } from './pages/professions';
import { TeamRoles } from './pages/team-roles';
import { Departments } from './pages/departments';
import { CompetencyMaps } from './pages/competency-maps';
import { CompetencyMapDetail } from './pages/competency-maps/_id';
import { ReviewRequests } from './pages/review-requests';
import { CompetencyReviews } from './pages/competency-reviews';
import { Competencies } from './pages/competencies';
import { AllGoals } from './pages/all-goals';
import { Auth } from './pages/auth';
import { Docs } from './pages/docs';
import { Policy } from './pages/policy';
import { useUserStore } from './store/userStore';
import { useUiStore } from './store/uiStore';
import { Error403, Error404 } from './components/errors/ErrorPage';
import { CalendarPage } from './pages/calendar';
import { useHasFeature } from './store/featureFlagsStore';
import { useCompanyModules, type CompanyModule } from './hooks/useCompanyModules';
import { COMING_SOON, PREVIEW_SOON_FLAG } from './config/comingSoon';
import { LanguageSwitcher } from './components/app/LanguageSwitcher';
import { ThemeToggle } from './components/app/ThemeToggle';
import { useDevice } from './hooks/useDevice';
import { ScrollLockProvider } from './contexts/ScrollLockContext';
import { useScrollLock } from './hooks/useScrollLock';
import { hasCompanyGrant, hasGrantOnOthers, useMyPermissions } from './hooks/usePermission';
import { OnboardingWizard } from './components/onboarding/OnboardingWizard';
import { PageTitle } from './components/app/PageTitle';
import { ImpersonationBanner } from './components/app/ImpersonationBanner';
import { AnalyticsProvider, analytics } from './analytics';
import { LearningCatalog } from './pages/learning';
import { LearningTracksAdmin } from './pages/learning/tracks';
import { CourseDetail } from './pages/learning/_id';
import { CourseEditor } from './pages/learning/_id/edit';
import { QuizPage } from './pages/learning/_id/quiz';
import { ScormPlayerPage } from './pages/learning/_id/scorm';
import { Review360Dashboard } from './pages/review-360';
import { Review360Templates } from './pages/review-360/templates';
import { Review360TemplateEditor } from './pages/review-360/templates/_id';
import { Review360CycleDetail } from './pages/review-360/_id';
import { Review360Respond } from './pages/review-360/_id/respond';
import { Review360ReportPage } from './pages/review-360/_id/report';
import { Essays } from './pages/essays';
import { EssaysManage } from './pages/essays/manage';
import { QuarterlyScoring } from './pages/quarterly-scoring';
import { QuarterlyScoringMatrix } from './pages/quarterly-scoring/matrix';
import { QuarterlyScoringResults } from './pages/quarterly-scoring/results';
import { FeatureFlagsPage } from './pages/settings/features';
import { refreshOwnSubscription } from './hooks/usePushApi';
import i18n from './i18n';
import { api } from './utils/api';

const analyticsConfig = {
  apiKey: process.env.REACT_APP_POSTHOG_KEY || 'phc_8vEho2aPV56fRnXhAYKjvLY6xQXB7u3VJ7Z3ZOJrZB6',
  host: process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
  app: 'skillscape' as const,
  enableRecording: process.env.NODE_ENV === 'production',
  enableHeatmaps: process.env.NODE_ENV === 'production',
  debug: process.env.NODE_ENV !== 'production',
};

const ProtectedRoute: React.FC = () => {
  const token = useUserStore((s) => s.token);
  const user = useUserStore((s) => s.user);
  const [isChecking, setIsChecking] = useState(true);
  const checkSession = useUserStore((s) => s.checkSession);

  useEffect(() => {
    const initSession = async () => {
      await checkSession();
      setIsChecking(false);
    };
    initSession();
  }, [checkSession]);

  // Обновить на сервере свою подписку на пуши, если браузер уже подписан на этого
  // человека. Новую или чужую не создаёт — это делает только кнопка.
  const userId = user?._id;
  useEffect(() => {
    if (userId) void refreshOwnSubscription().catch(() => undefined);
  }, [userId]);

  // Язык интерфейса хранится в профиле: на новом устройстве человек сразу видит
  // свой, а не язык браузера. Пока не выбирал ни разу — остаётся язык браузера.
  const userLocale = user?.locale;
  useEffect(() => {
    if (userLocale && userLocale !== i18n.language) void i18n.changeLanguage(userLocale);
  }, [userLocale]);

  // Любая смена языка (меню, настройки) уходит в профиль.
  useEffect(() => {
    if (!userId) return;
    const save = (lng: string) => {
      const { user: current, adminSession } = useUserStore.getState();
      // Админ, вошедший от имени сотрудника, не меняет ему язык.
      if ((lng !== 'ru' && lng !== 'en') || lng === current?.locale || adminSession) return;
      api('/users/me', { method: 'PATCH', body: { locale: lng } })
        .then(() => {
          const fresh = useUserStore.getState().user;
          if (fresh) useUserStore.getState().setUser({ ...fresh, locale: lng });
        })
        .catch(() => undefined);
    };
    i18n.on('languageChanged', save);
    return () => i18n.off('languageChanged', save);
  }, [userId]);

  if (isChecking) {
    return <div />;
  }

  if (!token || !user) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
};

/** Гард маршрута по праву с сервера (`GET /users/me/permissions`), а не по флагу admin. */
const RequirePermission: React.FC<{ permission: string; isOnOthers?: boolean }> = ({
  permission,
  isOnOthers = false,
}) => {
  // isPending, а не isLoading: пока профиль не загружен, запрос выключен — это ещё не «нет прав».
  const { data, isPending } = useMyPermissions();
  if (isPending) return <Loader m="xl" />;
  // isOnOthers: раздел, где право на своих людей тоже что-то показывает (аналитика лида) —
  // то же правило, что на бэке: вся компания или свои люди, если они есть.
  const isAllowed = isOnOthers
    ? hasGrantOnOthers(data, permission)
    : hasCompanyGrant(data, permission);
  return isAllowed ? <Outlet /> : <Error403 />;
};

/**
 * Гость на `/` внутри SPA (переход без перезагрузки): лендинг — статическая
 * страница вне бандла, её отдаёт nginx. Dev-сервер CRA так не умеет, поэтому
 * локально идём на сам файл.
 */
const LandingRedirect: React.FC = () => {
  useEffect(() => {
    window.location.replace(process.env.NODE_ENV === 'production' ? '/' : '/landing.html');
  }, []);
  return null;
};

/**
 * Раздел, не подключённый компании (`Tenant.modules`), закрыт и по прямой ссылке: на главную, как
 * бета-гейт. Пока разделы не пришли — ждём, а не пускаем и не выгоняем. Несколько — хватит любого.
 */
const ModuleGate: React.FC<{ module: CompanyModule | CompanyModule[] }> = ({ module }) => {
  const { hasModule, isLoaded } = useCompanyModules();
  if (!isLoaded) return <Loader m="xl" />;
  const isOn = ([] as CompanyModule[]).concat(module).some(hasModule);
  return isOn ? <Outlet /> : <Navigate to="/dashboard" replace />;
};

// Раздел «скоро» закрыт и по прямой ссылке, а не только в меню. Раздел, не подключённый
// компании, — на главную (ключи «скоро» совпадают с ключами разделов).
const SoonGate: React.FC<{ section: keyof typeof COMING_SOON }> = ({ section }) => {
  const isPreview = useHasFeature(PREVIEW_SOON_FLAG);
  const { hasModule, isLoaded } = useCompanyModules();
  if (isLoaded && !hasModule(section)) return <Navigate to="/dashboard" replace />;
  if (COMING_SOON[section] && !isPreview) return <Error404 />;
  return <ModuleGate module={section} />;
};

// Бета-функция закрыта и по прямой ссылке: без флага — на главную.
const FeatureGate: React.FC<{ feature: string }> = ({ feature }) =>
  useHasFeature(feature) ? <Outlet /> : <Navigate to="/dashboard" replace />;

const AppContent: React.FC = () => {
  const location = useLocation();
  const [opened, setOpened] = useState(false);
  const deviceType = useDevice();
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const token = useUserStore((s) => s.token);
  const isPreviewSoon = useHasFeature(PREVIEW_SOON_FLAG);
  const { hasModule } = useCompanyModules();
  const isLearningOpen = (!COMING_SOON.learning || isPreviewSoon) && hasModule('learning');

  // Блокируем скроллинг когда открыта мобильная навигация
  useScrollLock(opened && deviceType === 'mobile');

  // Закрываем мобильное меню при изменении маршрута
  useEffect(() => {
    setOpened(false);
  }, [location.pathname]);

  if (location.pathname === '/' && !token) {
    return <LandingRedirect />;
  }

  // SCORM player renders without AppShell. Пока «Обучение» закрыто — 404 внутри оболочки.
  if (isLearningOpen && location.pathname.match(/^\/learning\/[^/]+\/scorm$/)) {
    const token = useUserStore.getState().token;
    if (!token) {
      return <Navigate to="/auth" replace />;
    }
    return (
      <QueryClientProvider client={queryClient}>
        <PageTitle />
        {/* Внутри Routes: иначе useParams() у плеера пуст и курс не грузится. */}
        <Routes>
          <Route path="/learning/:id/scorm" element={<ScormPlayerPage />} />
        </Routes>
      </QueryClientProvider>
    );
  }

  // Public pages rendered without the AppShell sidebar.
  const isFullscreen =
    location.pathname === '/auth' ||
    location.pathname === '/demo' ||
    location.pathname === '/policy' ||
    location.pathname === '/docs' ||
    location.pathname.startsWith('/docs/');
  if (isFullscreen) {
    return (
      <QueryClientProvider client={queryClient}>
        <PageTitle />
        <Routes>
          {/* Вошедшему форма входа не нужна. Истёкший токен разлогинит первый же запрос. */}
          <Route path="/auth" element={token ? <Navigate to="/dashboard" replace /> : <Auth />} />
          {/* Демо: роли без кода, своя песочница. Вошедшему — тоже, с предупреждением. */}
          <Route path="/demo" element={<Auth isDemoEntry />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/docs/:articleId" element={<Docs />} />
        </Routes>
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <PageTitle />
      <OnboardingWizard />
      <AppShell
        navbar={{
          // Desktop (sm+) can collapse to an icon-only rail; mobile stays a
          // full-width drawer toggled by the burger.
          width: { base: 320, sm: sidebarCollapsed ? 84 : 320 },
          breakpoint: 'sm',
          collapsed: { mobile: !opened },
        }}
        padding="md"
        header={{ height: deviceType === 'mobile' ? 60 : 0 }}
      >
        {deviceType === 'mobile' && (
          <AppShell.Header className="mobile-header">
            <Group h="100%" px="md" justify="space-between">
              <Burger
                opened={opened}
                onClick={() => setOpened(!opened)}
                hiddenFrom="sm"
                size="sm"
              />
              <Logo showSubtitle={false} />
              <Group gap="xs" ml="auto">
                <ThemeToggle />
                <LanguageSwitcher />
              </Group>
            </Group>
          </AppShell.Header>
        )}

        <AppShell.Navbar>
          <Navigation />
        </AppShell.Navbar>
        <AppShell.Main>
          <ImpersonationBanner />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route element={<ProtectedRoute />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route element={<ModuleGate module="calendar" />}>
                <Route path="/calendar" element={<CalendarPage />} />
              </Route>
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/profile/:id" element={<UserProfile />} />
              <Route path="/profile/:id/competencies" element={<UserProfile />} />
              <Route path="/profile/:id/goals" element={<UserProfile />} />
              <Route path="/profile/:id/learning" element={<UserProfile />} />
              <Route path="/profile/competencies" element={<UserProfile />} />
              <Route path="/profile/goals" element={<UserProfile />} />
              <Route path="/profile/learning" element={<UserProfile />} />
              {/*<Route path="/change-password" element={<ChangePassword />} />*/}
              <Route path="/settings" element={<Settings />} />
              <Route element={<ModuleGate module="performanceReview" />}>
                <Route path="/performance-review" element={<PerformanceReview />} />
                <Route
                  path="/performance-review/forms/:id"
                  element={<PerformanceReviewFormPage />}
                />
                {/* Старая страница раунда удалена; адрес оставлен ради сохранённых ссылок. */}
                <Route
                  path="/performance-review/:id"
                  element={<Navigate to="/performance-review" replace />}
                />
                <Route
                  path="/performance-review/:id/results"
                  element={<PerformanceReviewRoundResults />}
                />
              </Route>
              {/* /tech-radar пока «скоро» в меню: роут не подключён, страница лежит в
                  pages/tech-radar с данными-примером. */}
              <Route path="/settings/features" element={<FeatureFlagsPage />} />
              <Route element={<ModuleGate module={['essays', 'quarterly']} />}>
                <Route path="/essays" element={<Essays />} />
                <Route path="/essays/manage" element={<EssaysManage />} />
              </Route>
              <Route element={<ModuleGate module="performanceReview" />}>
                <Route element={<FeatureGate feature="reviewAnalytics" />}>
                  <Route element={<RequirePermission permission="reviews.analytics" isOnOthers />}>
                    <Route path="/analytics" element={<Analytics />} />
                  </Route>
                </Route>
              </Route>
              <Route element={<ModuleGate module="quarterly" />}>
                <Route path="/quarterly-scoring" element={<QuarterlyScoring />} />
                <Route element={<RequirePermission permission="quarterly.scores.results" />}>
                  <Route path="/quarterly-scoring/results" element={<QuarterlyScoringResults />} />
                </Route>
                <Route element={<RequirePermission permission="quarterly.scores.manage" />}>
                  <Route path="/quarterly-scoring/matrix" element={<QuarterlyScoringMatrix />} />
                </Route>
              </Route>
              <Route element={<ModuleGate module="competencyMap" />}>
                <Route path="/review-requests" element={<ReviewRequests />} />
                <Route path="/grades" element={<CompetencyMap />} />
                <Route path="/competency-maps/:mapId/:gradeId" element={<GradeDetail />} />
                {/* Таблица грейдов класса — доступна на чтение любому (напр. ревьюверу
                    чужого класса). Мутации внутри страницы — по праву maps.manage. */}
                <Route path="/competency-maps/:id" element={<CompetencyMapDetail />} />
              </Route>

              {/* LMS routes */}
              <Route element={<SoonGate section="learning" />}>
                <Route path="/learning" element={<LearningCatalog />} />
                <Route path="/learning/:id" element={<CourseDetail />} />
                <Route path="/learning/:id/quiz" element={<QuizPage />} />
              </Route>

              {/* Review 360 */}
              <Route element={<SoonGate section="review360" />}>
                <Route path="/review-360" element={<Review360Dashboard />} />
                <Route
                  path="/review-360/:cycleId/respond/:responseId"
                  element={<Review360Respond />}
                />
                <Route path="/review-360/:id/report/:subjectId" element={<Review360ReportPage />} />
                <Route path="/review-360/:id" element={<Review360CycleDetail />} />
              </Route>

              {/* Управление обучением — право learning.manage, а не флаг admin */}
              <Route element={<RequirePermission permission="learning.manage" />}>
                <Route element={<SoonGate section="learning" />}>
                  <Route path="/learning/:id/edit" element={<CourseEditor />} />
                  <Route path="/learning/tracks" element={<LearningTracksAdmin />} />
                </Route>
              </Route>

              {/* Админская страница заявок — право competencies.review, а не флаг admin */}
              <Route element={<ModuleGate module="competencyMap" />}>
                <Route element={<RequirePermission permission="competencies.review" />}>
                  <Route path="/competency-reviews" element={<CompetencyReviews />} />
                </Route>
              </Route>

              {/* Люди, структура, карты и настройки — по правам с сервера, а не по флагу admin:
                  владелец без флага видит всё, «Настройщик» — структуру и карты. */}
              <Route element={<RequirePermission permission="people.manage" />}>
                <Route path="/users" element={<Users />} />
              </Route>
              <Route element={<RequirePermission permission="company.structure" />}>
                <Route path="/teams" element={<Teams />} />
                {/* Бета: без флага `teamRolesDictionary` страница уводит на /teams. */}
                <Route path="/teams/roles" element={<TeamRoles />} />
                <Route path="/professions" element={<Professions />} />
                <Route path="/departments" element={<Departments />} />
              </Route>
              <Route element={<ModuleGate module="competencyMap" />}>
                <Route element={<RequirePermission permission="maps.manage" />}>
                  <Route path="/competency-maps" element={<CompetencyMaps />} />
                  <Route path="/competencies" element={<Competencies />} />
                </Route>
              </Route>
              <Route element={<RequirePermission permission="company.settings" />}>
                <Route path="/settings/organization" element={<OrganizationSettings />} />
              </Route>

              {/* «Роли и права» (бета): роли — company.roles, журнал — company.audit */}
              <Route element={<FeatureGate feature="rolesScreen" />}>
                <Route element={<RequirePermission permission="company.roles" />}>
                  <Route path="/roles" element={<RolesPage />} />
                  <Route path="/roles/people" element={<RolesPage />} />
                  <Route path="/roles/why" element={<RolesPage />} />
                </Route>
                <Route element={<RequirePermission permission="company.audit" />}>
                  <Route path="/roles/audit" element={<RolesPage />} />
                </Route>
              </Route>

              {/* Шаблоны 360 — право review360.manage, а не флаг admin */}
              <Route element={<RequirePermission permission="review360.manage" />}>
                <Route element={<SoonGate section="review360" />}>
                  <Route path="/review-360/templates" element={<Review360Templates />} />
                  <Route path="/review-360/templates/:id" element={<Review360TemplateEditor />} />
                </Route>
              </Route>

              {/* Все цели компании — goals.read на компанию, как `GET /goals/admin/all` */}
              <Route element={<ModuleGate module="goals" />}>
                <Route element={<RequirePermission permission="goals.read" />}>
                  <Route path="/all-goals" element={<AllGoals />} />
                </Route>
              </Route>

              {/* Раунды и анкеты ревью — право reviews.manage (владелец и «Настройщик» тоже) */}
              <Route element={<ModuleGate module="performanceReview" />}>
                <Route element={<RequirePermission permission="reviews.manage" />}>
                  <Route path="/performance-review/admin" element={<PerformanceReviewAdmin />} />
                  <Route
                    path="/performance-review/admin/questionnaires"
                    element={<PerformanceReviewQuestionnaires />}
                  />
                  <Route
                    path="/performance-review/admin/:id"
                    element={<PerformanceReviewAdminRound />}
                  />
                </Route>
              </Route>
              {/* 404 */}
              <Route path="*" element={<Error404 />} />
            </Route>
          </Routes>
        </AppShell.Main>
      </AppShell>
    </QueryClientProvider>
  );
};

const App: React.FC = () => (
  <Router>
    <ScrollLockProvider>
      <AnalyticsProvider provider={analytics} config={analyticsConfig}>
        <AppContent />
      </AnalyticsProvider>
    </ScrollLockProvider>
  </Router>
);

export default App;
